/* eslint-disable max-lines */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useCallback, useState, useEffect } from 'react'

import classNames from 'classnames'
import { isEmpty } from 'ramda'
import ReactSelect, {
  ActionMeta,
  createFilter,
  GroupBase,
  MultiValue as MultiValueType,
  OnChangeValue,
  StylesConfig,
  SelectInstance,
  PropsValue,
  InputActionMeta,
} from 'react-select'
import { v4 } from 'uuid'

import { SelectOptionInterface, SelectProps } from '@customTypes'
import {
  LabelGuard,
  FloatingLabelGuard,
} from '@customTypes/components/_helpers'
import { HelpText, InputContent, InputWrapper } from '_atoms'
import { useDeepMemo } from '_hooks'

import { getSelectValue, getSortedOptions } from './functions'
import { constants } from './helpers'
import {
  getMenuList,
  getClearIndicator,
  getDropdownIndicator,
  getIndicatorSeparator,
  getOptions,
  MultiValueRemove as MultiValueRemoveComponent,
  ValueContainer as ValueContainerComponent,
  MultiValue as MultiValueComponent,
  getControl,
  getNoOptionsMessage,
} from './select-components'
import { getStyles } from './styles'

const filterOption = createFilter({
  ignoreAccents: false,
})

type Guards = LabelGuard & FloatingLabelGuard

export const Select = React.forwardRef(
  <
    Opt extends SelectOptionInterface,
    IsMulti extends boolean,
    Group extends GroupBase<Opt>
  >(
    { className, ...props }: SelectProps<Opt, IsMulti, Group> & Guards,
    ref: React.ForwardedRef<SelectInstance<Opt, IsMulti, Group>>
  ): JSX.Element => {
    const {
      options,
      isMulti,
      dropdownIcons = {
        iconOpen: 'chevron-up',
        iconClosed: 'chevron-down',
      },
      hideDropdownIcon = false,
      shouldHideSeparator,
      clearTitle,
      defaultValue,
      onSelect,
      onChangeRaw,
      onChange,
      value,
      shouldVirtualize,
      slim,
      hasError: defaultHasError,
      helpText,
      errorText,
      float,
      displayIcon,
      disableWhenHasValue,
      defaultInputValue,
      inputValue,
      onInputChange,
      openOnSearch,
      clearIcon = 'x-circle',
      isSearchable,
      menuIsOpen,
      id,
      noResultsFoundMessage,
    } = props
    const [search, setSearch] = useState(defaultInputValue)

    useEffect(() => {
      setSearch(inputValue)
    }, [inputValue])

    const handleInputChange = useCallback(
      (newValue: string, actionMeta: InputActionMeta): void | undefined => {
        setSearch(newValue)

        if (onInputChange) {
          onInputChange(newValue, actionMeta)
        }
      },
      [onInputChange, setSearch]
    )

    if (disableWhenHasValue && isMulti) {
      throw new Error("Cannot use 'disableWhenHasValue' when isMulti is set")
    }

    const hasError = defaultHasError || (!!errorText && !isEmpty(errorText))

    const initialValue = useMemo(
      () => getSelectValue(options, defaultValue),
      [defaultValue]
    )
    const currValue = useMemo(
      () => getSelectValue(options, value),
      [options, value]
    )

    const onChangeFn = useCallback(
      (newValue: OnChangeValue<Opt, IsMulti>, actionMeta: ActionMeta<Opt>) => {
        if (onChangeRaw) onChangeRaw(newValue, actionMeta)

        if (onChange && !newValue) {
          onChange(null)
        }

        if (onChange && !!newValue)
          onChange(
            isMulti
              ? (newValue as MultiValueType<Opt>).map(el => el.value)
              : (newValue as Opt).value
          )

        if (!onSelect) {
          return
        }

        if (!newValue) {
          onSelect(null)
          return
        }

        onSelect(
          isMulti
            ? (newValue as MultiValueType<Opt>).map(el => el.value)
            : (newValue as Opt).value
        )
      },
      [onChangeRaw, onChange, onSelect, isMulti]
    )

    const { sortedOptions, lastSelectedOptions } = useMemo(
      () => getSortedOptions(props.options, isMulti, value || defaultValue),
      [props.options, value, props.isMulti]
    )

    const closeMenuOnSelect = !isMulti

    const components = useDeepMemo(
      () => ({
        ClearIndicator: getClearIndicator(clearTitle, clearIcon),
        DropdownIndicator: getDropdownIndicator({
          config: dropdownIcons,
          disableWhenHasValue,
          hideDropdownIcon,
        }),
        IndicatorSeparator: getIndicatorSeparator({
          shouldHide: shouldHideSeparator || !props.isClearable,
          disableWhenHasValue,
        }),
        MenuList: getMenuList(shouldVirtualize),
        MultiValueRemove: MultiValueRemoveComponent,
        Option: getOptions(lastSelectedOptions),
        ValueContainer: ValueContainerComponent,
        MultiValue: MultiValueComponent,
        Control: getControl({ float, displayIcon }),
        NoOptionsMessage: getNoOptionsMessage(noResultsFoundMessage),
      }),
      [
        clearTitle,
        clearIcon,
        shouldVirtualize,
        dropdownIcons,
        shouldHideSeparator,
        props.isClearable,
        lastSelectedOptions,
        float,
        noResultsFoundMessage,
      ]
    )

    const styles = useMemo(
      () =>
        getStyles({
          slim,
          hasError,
          float,
          hasNoOptionsMessage: !!noResultsFoundMessage,
        }) as StylesConfig<Opt, IsMulti, Group>,
      [hasError, slim, float, noResultsFoundMessage]
    )

    const selectClassName =
      className &&
      className.split(' ').reduce<string>((acc, curr, i) => {
        const newClass = curr.endsWith('--select') ? curr : `${curr}--select`

        return i === 0 ? newClass : `${acc} ${newClass}`
      }, '')

    const errorArr =
      errorText && Array.isArray(errorText) ? errorText : [errorText]
    const helpArr = helpText && Array.isArray(helpText) ? helpText : [helpText]

    const selectedOption = value === undefined ? defaultValue : value
    const hasValue = Array.isArray(selectedOption)
      ? selectedOption.length !== 0
      : !!selectedOption

    /**
     * Prevent menu from opening if:
     * - disableWhenHasValue is set and there is an option selected
     * - openOnSearch is set and there isn't any search value
     *  */
    const forceMenuIsOpen =
      (disableWhenHasValue && hasValue) || (openOnSearch && !search)
        ? false
        : menuIsOpen

    // Remove input when flag disableWhenHasValue is set and there is an option selected
    const forceIsSearchable =
      disableWhenHasValue && hasValue ? false : isSearchable

    return (
      <InputWrapper
        className={classNames(className, 'centivo--select__wrapper')}
      >
        <InputContent>
          <ReactSelect
            {...props}
            inputId={id}
            ref={ref}
            options={sortedOptions}
            onChange={onChangeFn}
            filterOption={filterOption}
            classNamePrefix={constants.CLASSNAME_PREFIX}
            components={components}
            className={classNames(selectClassName, 'centivo--select')}
            closeMenuOnSelect={closeMenuOnSelect}
            defaultValue={initialValue}
            hideSelectedOptions={false}
            value={currValue as PropsValue<Opt> | undefined}
            blurInputOnSelect={!isMulti}
            styles={styles}
            menuIsOpen={forceMenuIsOpen}
            isSearchable={forceIsSearchable}
            onInputChange={handleInputChange}
            openMenuOnFocus
          />
        </InputContent>
        {errorArr?.map(
          err => err && <HelpText key={v4()} text={err} hasError />
        )}
        {helpArr?.map(
          text => text && <HelpText key={v4()} text={text as string} />
        )}
      </InputWrapper>
    )
  }
)

Select.displayName = 'CentivoSelect'
